import { useEffect } from 'react';
import { useMutation } from 'react-query';
import { useUpdateAtom } from 'jotai/utils';
import chunk from 'lodash/chunk';

import { getLikedProductIdList } from '../graphql/related-item-list.2';
import { likeLookupAtom } from '../store/likeLookupAtom';

const API_LIMIT = 100;

const fetchLikedProductIdList = async (idList: string[]) => {
  const chunkedIdList = chunk(idList, API_LIMIT);
  const results = await Promise.allSettled(chunkedIdList.map((id_list) => getLikedProductIdList({ id_list })));

  return results.reduce((acc, result) => {
    if (result.status === 'rejected') {
      return acc;
    }
    const {
      value: {
        data: { saved_product_id_list },
      },
    } = result;
    if (saved_product_id_list?.catalog_product_id_list) {
      saved_product_id_list.catalog_product_id_list.forEach((id) => {
        acc[id] = true;
      });
    }
    return acc;
  }, {} as Record<string, boolean>);
};

interface Config<T> {
  data?: T;
  convert: (data: T) => string[];
}

export const useInitLikedProducts = <T>({ data, convert }: Config<T>): void => {
  const update = useUpdateAtom(likeLookupAtom);

  const { mutate } = useMutation({
    mutationFn: fetchLikedProductIdList,
    onSuccess: (lookup) => {
      update((prev) => ({ ...prev, ...lookup }));
    },
  });

  useEffect(() => {
    if (data) {
      const list = convert(data);
      list.length > 0 && mutate(list);
    }
  }, [mutate, data, convert]);
};
